import classNames from "classnames/bind";
import { isNaN } from "es-toolkit/compat";
import React from "react";
import { Icon } from "../../Icon";
import { colours } from "../../constants/colours";
import { DataDisplayWithAdornment } from "../_components/DataDisplayWithAdornment";
import styles from "./TextDataDisplay.module.scss";
import type { TextDataDisplayProps } from "./types";
import * as Tooltip from "@radix-ui/react-tooltip";

const cx = classNames.bind(styles);

const TextDataDisplay = ({
  value,
  alignment = "left",
  clamp,
  emptyText = "-",
  showInvalidIndicator = true,
  invalidText = "Unsupported data type",
  className,
  style,
  isInline = false,
  isBusy = false,
  isLoading = false,
  as: asProp,
}: TextDataDisplayProps) => {
  const trueValue = String(value);
  const isEmpty = value === null || value === "" || value === undefined;
  const isInvalid = trueValue === null || isNaN(trueValue);
  const CustomNode = asProp ?? "div";

  return (
    <DataDisplayWithAdornment
      showAdornment={showInvalidIndicator && !isEmpty && isInvalid}
      alignment={alignment}
      isInline={isInline}
      isBusy={isBusy}
      isLoading={isLoading}
      adornmentSlot={({ iconProps, className }) => (
        <span className={className}>
          <Icon
            {...iconProps}
            variant="warning"
            colour={colours.status.warning}
            alt={invalidText}
            aria-roledescription="Warning Icon"
          />
        </span>
      )}
      className={cx(className, isInvalid && "bare")}
      style={style}
    >
      {isEmpty || isInvalid ? (
        emptyText
      ) : clamp ? (
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <CustomNode
                className={cx("clamped")}
                style={{ WebkitLineClamp: clamp }}
              >
                {trueValue}
              </CustomNode>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className={cx("tooltip-content")}
                sideOffset={5}
                collisionPadding={16}
              >
                {trueValue}
                <Tooltip.Arrow className={cx("tooltip-arrow")} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ) : (
        <>{trueValue}</>
      )}
    </DataDisplayWithAdornment>
  );
};

export default TextDataDisplay;
